import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { BaseState, withCrudOperations } from '../crud.state';
import { BookmarkCategory } from '../../models/bookmark-category';
import { withBookmarkCategorySelectors } from './bookmark-category.selectors';
import { BookmarkCategoryService } from '../../services/bookmark-category/bookmark-category.service';
import { effect, inject } from '@angular/core';
import { ActiveUserStore } from '../user/active-user.state';
import { BookmarkStore } from '../bookmark/bookmark.state';
import { StoryStore } from '../story/story.state';
import { withLogging } from '../features/logger.feature';
import { withWorkspaceHelpers } from '../workspace-helper.selectors';
import { SocketService } from '../../services/socket.service';
import { ConfirmationDialogService } from '../../services/confirmation-dialog.service';

export type BookmarkCategoryState = BaseState<BookmarkCategory>;

export const initialState: BookmarkCategoryState = {
  items: {},
  loading: false,
  selectedId: '',
  selection: [],
  initialized: false,
  itemsLoaded: false,
  version: 0,
  lastPersistedId: '',
  trigger: undefined,
  entityType: 'BookmarkCategory',
  entityLabel: 'Themenbereich',
  filter: {
    query: '',
    category: [],
    tags: undefined,
    products: undefined,
    published: null,
    locked: null,
    ids: undefined,
  },
};

export const BookmarkCategoryStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCrudOperations<BookmarkCategory>(BookmarkCategoryService),
  withLogging(initialState.entityType),
  withWorkspaceHelpers(),
  withMethods(
    (
      store,
      bookmarkCategoryService = inject(BookmarkCategoryService),
      socketService = inject(SocketService),
      confirmationDialogService = inject(ConfirmationDialogService)
    ) => ({
      isPrivate(categoryId: string) {
        return store.items()[categoryId]?.private || false;
      },

      /**
       * Adds/removes bookmark ids on one category via the atomic server
       * endpoint and patches the store with the server's resulting array.
       * Unlike `update()` (an rxMethod with switchMap), several calls in a
       * row don't cancel each other, and a stale local copy of the
       * category can't overwrite assignments made elsewhere in the meantime.
       *
       * @param {string} categoryId - The category to change.
       * @param {{ add?: string[]; remove?: string[] }} changes - Bookmark ids to add/remove.
       * @return {Promise<void>} Resolves once the store is patched.
       */
      async updateBookmarkAssignments(
        categoryId: string,
        changes: { add?: string[]; remove?: string[] }
      ): Promise<void> {
        try {
          const updated =
            await bookmarkCategoryService.updateBookmarkAssignments(
              categoryId,
              changes
            );
          if (!updated?._id) return;
          patchState(store, {
            items: { ...store.items(), [updated._id]: updated },
            action: 'update',
            lastPersistedId: updated._id,
            lastPersistedLabel: updated.title,
          });
          socketService.emitEvent('baseEvent', {
            action: 'update',
            commune: 'dev',
            body: updated,
            type: bookmarkCategoryService.instanceType,
            trigger: undefined,
          });
        } catch (error) {
          patchState(store, { error });
          confirmationDialogService.addErrorMessage(
            'Die Zuordnung zum Themenbereich konnte nicht gespeichert werden.',
            'Speichern fehlgeschlagen'
          );
        }
      },
    })
  ),
  withBookmarkCategorySelectors(),
  withHooks({
    onInit(store, activeUserStore = inject(ActiveUserStore)) {
      // This store is reachable very early, before any commune is known:
      // the always-mounted ToolOverlayComponent injects RoutingService,
      // whose constructor injects TopicHelperService, which field-injects
      // this store directly - well before the public-Atlas route guard
      // resolves the commune from the slug. Watch for commune to become
      // available (or change) instead of firing once at construction.
      effect(
        () => {
          if (activeUserStore.commune()) {
            store.loadAllItemsByPromise();
          }
        },
        { allowSignalWrites: true }
      );
    },
  })
);
